/**
 * PersonaLab - HubSpot Official Forms Integration Service
 * Dispara submissões diretas para a API pública de formulários do HubSpot (Headless).
 * Não afeta a interface nem bloqueia a aplicação caso a rede oscile.
 */

import { getStateFromPhone, getUserTimeZone } from '@/lib/geoUtils';
import { getCompanyInfoFromEmail } from '@/lib/companyEnrichment';

export const HUBSPOT_PORTAL_ID = '51409959';
export const HUBSPOT_SIGNUP_FORM_ID = '9e6d84c8-843c-4374-a881-b1e963a81a4d';
export const HUBSPOT_LOGIN_FORM_ID = import.meta.env.VITE_HUBSPOT_LOGIN_FORM_ID || '88af3fcc-02d0-415f-8a08-ef6d6e94b16d';
export const HUBSPOT_PROFILE_FORM_ID = import.meta.env.VITE_HUBSPOT_PROFILE_FORM_ID || '22304683-53ca-4626-8667-fb78957740e1';
export const HUBSPOT_USAGE_FORM_ID = import.meta.env.VITE_HUBSPOT_USAGE_FORM_ID || '9c5a4337-055a-4a78-af09-db4c8bc275c3';
export const HUBSPOT_SUBSCRIPTION_TYPE_ID = Number(import.meta.env.VITE_HUBSPOT_SUBSCRIPTION_TYPE_ID || 2614574167);

export function getHubspotLegalConsentOptions(subscriptionTypeId: number = HUBSPOT_SUBSCRIPTION_TYPE_ID) {
  return {
    legitimateInterest: {
      value: true,
      subscriptionTypeId,
      subscriptionTypeIds: [subscriptionTypeId],
      legalBasis: 'CUSTOMER',
      text: 'Armazenaremos e processaremos estas informações para fornecer nossos produtos e serviços. Você pode cancelar a qualquer momento.',
    },
  };
}

export interface HubSpotSignupPayload {
  email: string;
  firstname: string;
  lastname?: string;
  phone?: string | null;
  total_logins?: number;
  ultimo_login?: string;
}

export interface HubSpotLoginSubmitPayload {
  email: string;
  total_logins: number;
  ultimo_login: string;
  formId?: string;
}

export interface HubSpotProfilePayload {
  email: string;
  firstname?: string;
  lastname?: string;
  phone?: string | null;
  cargo?: string | null;
  tamanho_empresa?: string | null;
  objetivo_principal?: string | null;
  como_conheceu?: string | null;
  perfil_completo?: string | null;
  company?: string | null;
  lifecyclestage?: string;
  hs_lead_status?: string;
  pageUri?: string;
  pageName?: string;
  formId?: string;
}

export interface HubSpotUsagePayload {
  email: string;
  userId?: string;
  total_personas: number;
  total_contextos: number;
  total_conversas: number;
  total_insights: number;
  lifecyclestage?: string;
  hs_lead_status?: string;
  closedate?: string;
  formId?: string;
}

/**
 * Divide o nome completo em primeiro nome e sobrenome.
 */
export function splitFullName(fullName?: string | null): { firstname: string; lastname: string } {
  if (!fullName || !fullName.trim()) {
    return { firstname: '', lastname: '' };
  }
  const parts = fullName.trim().split(/\s+/);
  const firstname = parts[0] || '';
  const lastname = parts.slice(1).join(' ') || '';
  return { firstname, lastname };
}

/**
 * Formata data e hora para exibição amigável no CRM (ex: "06/09/2026 às 01:42:00").
 */
export function formatHubspotDateTime(dateInput: Date | number = new Date()): string {
  const date = typeof dateInput === 'number' ? new Date(dateInput) : dateInput;
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(date).replace(',', ' às');
}

/**
 * Recupera o cookie de rastreamento do HubSpot (hubspotutk) se presente no navegador.
 * Permite associar o histórico prévio de navegação (origem do tráfego) ao novo lead.
 */
export function getHubspotUtk(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)hubspotutk=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Envia os dados do formulário de cadastro diretamente para a API Oficial de Formulários do HubSpot.
 * Padrão Fail-Safe: erros de rede não bloqueiam o fluxo do usuário na plataforma.
 */
export async function submitHubspotSignupForm(data: HubSpotSignupPayload): Promise<boolean> {
  try {
    const formattedDate = data.ultimo_login || formatHubspotDateTime();
    const fields = [
      { name: 'email', value: data.email },
      { name: 'firstname', value: data.firstname },
      { name: 'lastname', value: data.lastname || data.firstname || 'Sobrenome' },
      { name: 'total_logins', value: String(data.total_logins ?? 1) },
      { name: 'ultimo_login', value: formattedDate },
    ];

    if (data.phone) {
      fields.push({ name: 'phone', value: data.phone });
      const state = getStateFromPhone(data.phone);
      if (state) {
        fields.push({ name: 'state', value: state });
      }
    }

    // Propriedades nativas de ciclo de vida e localização
    fields.push({ name: 'lifecyclestage', value: 'subscriber' });
    fields.push({ name: 'hs_lead_status', value: 'NEW' });
    fields.push({ name: 'country', value: 'Brasil' });
    fields.push({ name: 'hs_timezone', value: getUserTimeZone() });
    fields.push({ name: 'hs_language', value: 'pt-br' });
    fields.push({ name: 'hs_legal_basis', value: 'legitimate_interest_customer' });

    // Enriquecimento automático de domínio corporativo B2B
    const companyInfo = getCompanyInfoFromEmail(data.email);
    if (companyInfo.isCorporate) {
      if (companyInfo.website) {
        fields.push({ name: 'website', value: companyInfo.website });
      }
      if (companyInfo.companyName) {
        fields.push({ name: 'company', value: companyInfo.companyName });
      }
    }

    const hutk = getHubspotUtk();

    const payload = {
      fields,
      context: {
        ...(hutk ? { hutk } : {}),
        pageUri: typeof window !== 'undefined' ? window.location.href : 'https://app.chatpersonalab.com.br/signup',
        pageName: typeof document !== 'undefined' ? document.title : 'Cadastro PersonaLab',
      },
      legalConsentOptions: getHubspotLegalConsentOptions(),
    };

    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_SIGNUP_FORM_ID}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('[HubSpot] Erro na submissão do formulário:', response.status, errorText);
      return false;
    }

    return true;
  } catch (err) {
    console.warn('[HubSpot] Falha na requisição de formulário (fail-safe):', err);
    return false;
  }
}

/**
 * Envia a telemetria de login de forma silenciosa para o formulário dedicado de login no HubSpot.
 * Não envia e-mails de alerta e atualiza diretamente total_logins e ultimo_login no CRM.
 */
export async function submitHubspotLoginForm(data: HubSpotLoginSubmitPayload): Promise<boolean> {
  const formId = data.formId !== undefined ? data.formId : HUBSPOT_LOGIN_FORM_ID;
  if (!formId) {
    // Se o formulário silencioso ainda não foi configurado, não bloqueia o fluxo
    return false;
  }

  try {
    const fields = [
      { name: 'email', value: data.email },
      { name: 'total_logins', value: String(data.total_logins) },
      { name: 'ultimo_login', value: data.ultimo_login },
    ];

    const hutk = getHubspotUtk();

    const payload = {
      fields,
      context: {
        ...(hutk ? { hutk } : {}),
        pageUri: typeof window !== 'undefined' ? window.location.href : 'https://app.chatpersonalab.com.br/login',
        pageName: typeof document !== 'undefined' ? document.title : 'Login PersonaLab',
      },
    };

    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${formId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('[HubSpot] Erro na submissão do formulário silencioso de login:', response.status, errorText);
      return false;
    }

    return true;
  } catch (err) {
    console.warn('[HubSpot] Falha no formulário silencioso de login (fail-safe):', err);
    return false;
  }
}

/**
 * Envia as atualizações de perfil e onboarding para o formulário dedicado no HubSpot CRM.
 * Coleta: email, firstname, lastname, phone, cargo/jobtitle, tamanho_empresa, objetivo_principal, como_conheceu, perfil_completo.
 * Ativa alertas por e-mail no HubSpot (Opção B).
 * Padrão Fail-Safe: não bloqueia o fluxo do usuário caso a rede oscile.
 */
export async function submitHubspotProfileForm(data: HubSpotProfilePayload): Promise<boolean> {
  const formId = data.formId !== undefined ? data.formId : (HUBSPOT_PROFILE_FORM_ID || HUBSPOT_LOGIN_FORM_ID);
  if (!formId) {
    return false;
  }

  try {
    const fields: Array<{ name: string; value: string }> = [
      { name: 'email', value: data.email },
    ];

    if (data.firstname) {
      fields.push({ name: 'firstname', value: data.firstname });
    }
    if (data.lastname) {
      fields.push({ name: 'lastname', value: data.lastname });
    }
    if (data.phone) {
      fields.push({ name: 'phone', value: data.phone });
    }
    if (data.cargo) {
      // Envia tanto jobtitle (propriedade padrão nativa do HubSpot) quanto cargo (se criada customizada)
      fields.push({ name: 'jobtitle', value: data.cargo });
      fields.push({ name: 'cargo', value: data.cargo });
    }
    if (data.tamanho_empresa) {
      // Envia tanto company_size (propriedade padrão nativa "Porte da empresa") quanto tamanho_empresa
      fields.push({ name: 'company_size', value: data.tamanho_empresa });
      fields.push({ name: 'tamanho_empresa', value: data.tamanho_empresa });
    }
    if (data.objetivo_principal) {
      fields.push({ name: 'objetivo_principal', value: data.objetivo_principal });
    }
    if (data.como_conheceu) {
      fields.push({ name: 'como_conheceu', value: data.como_conheceu });
    }
    if (data.perfil_completo) {
      fields.push({ name: 'perfil_completo', value: data.perfil_completo });
    }

    // Propriedades nativas de geolocalização e base jurídica
    fields.push({ name: 'country', value: 'Brasil' });
    fields.push({ name: 'hs_timezone', value: getUserTimeZone() });
    fields.push({ name: 'hs_language', value: 'pt-br' });
    fields.push({ name: 'hs_legal_basis', value: 'legitimate_interest_customer' });

    if (data.phone) {
      const state = getStateFromPhone(data.phone);
      if (state) {
        fields.push({ name: 'state', value: state });
      }
    }

    // Enriquecimento corporativo B2B
    const companyInfo = getCompanyInfoFromEmail(data.email);
    if (data.company) {
      fields.push({ name: 'company', value: data.company });
    } else if (companyInfo.isCorporate && companyInfo.companyName) {
      fields.push({ name: 'company', value: companyInfo.companyName });
    }
    if (companyInfo.isCorporate && companyInfo.website) {
      fields.push({ name: 'website', value: companyInfo.website });
    }

    // Ciclo de vida e status de vendas
    if (data.lifecyclestage) {
      fields.push({ name: 'lifecyclestage', value: data.lifecyclestage });
    }
    if (data.hs_lead_status) {
      fields.push({ name: 'hs_lead_status', value: data.hs_lead_status });
    }

    const hutk = getHubspotUtk();

    const payload = {
      fields,
      context: {
        ...(hutk ? { hutk } : {}),
        pageUri: data.pageUri || (typeof window !== 'undefined' ? window.location.href : 'https://app.chatpersonalab.com.br/settings'),
        pageName: data.pageName || (typeof document !== 'undefined' ? document.title : 'Perfil PersonaLab'),
      },
      legalConsentOptions: getHubspotLegalConsentOptions(),
    };

    // 1. Atualiza também a fila de telemetria do HubSpot no navegador (_hsq)
    if (typeof window !== 'undefined' && window._hsq) {
      const identifyProps: Record<string, unknown> = { email: data.email };
      if (data.firstname) identifyProps.firstname = data.firstname;
      if (data.lastname) identifyProps.lastname = data.lastname;
      if (data.phone) identifyProps.phone = data.phone;
      if (data.cargo) {
        identifyProps.jobtitle = data.cargo;
        identifyProps.cargo = data.cargo;
      }
      if (data.tamanho_empresa) {
        identifyProps.company_size = data.tamanho_empresa;
        identifyProps.tamanho_empresa = data.tamanho_empresa;
      }
      if (data.objetivo_principal) identifyProps.objetivo_principal = data.objetivo_principal;
      if (data.como_conheceu) identifyProps.como_conheceu = data.como_conheceu;
      if (data.perfil_completo) identifyProps.perfil_completo = data.perfil_completo;

      window._hsq.push(['identify', identifyProps]);
      window._hsq.push(['trackPageView']);
    }

    // 2. Submete para a Forms API pública do HubSpot
    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${formId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('[HubSpot] Erro na submissão do formulário de perfil:', response.status, errorText);
      return false;
    }

    return true;
  } catch (err) {
    console.warn('[HubSpot] Falha no formulário de perfil (fail-safe):', err);
    return false;
  }
}

export interface HubSpotLoginTelemetryPayload {
  email: string;
  userId?: string;
  loginTimestamp?: number;
}

export interface HubSpotLoginTelemetryResult {
  totalLogins: number;
  ultimoLogin: number;
  formattedUltimoLogin: string;
}

/**
 * Retorna os dados atuais de telemetria de login salvos para o usuário.
 */
export function getHubspotLoginTelemetry(keyPrefix?: string): HubSpotLoginTelemetryResult {
  if (typeof window === 'undefined') {
    const now = Date.now();
    return { totalLogins: 1, ultimoLogin: now, formattedUltimoLogin: formatHubspotDateTime(now) };
  }
  const prefix = keyPrefix ? keyPrefix.toLowerCase() : 'default';
  const storageKey = `personalab_total_logins_${prefix}`;
  const lastLoginKey = `personalab_last_login_${prefix}`;

  let currentTotal = parseInt(localStorage.getItem(storageKey) || '1', 10);
  if (isNaN(currentTotal) || currentTotal < 1) currentTotal = 1;

  let lastLogin = parseInt(localStorage.getItem(lastLoginKey) || '0', 10);
  if (isNaN(lastLogin) || lastLogin <= 0) {
    lastLogin = Date.now();
  }

  return {
    totalLogins: currentTotal,
    ultimoLogin: lastLogin,
    formattedUltimoLogin: formatHubspotDateTime(lastLogin),
  };
}

/**
 * Registra o login do usuário no HubSpot CRM:
 * - Define a data e hora do último login (ultimo_login) como texto legível no CRM (ex: "06/09/2026 às 01:48:00")
 * - Incrementa o total de logins (total_logins / total_de_logins) no CRM
 * - Utiliza sessionStorage para evitar incrementos redundantes em simples F5 / reload de página
 */
export function trackHubspotLogin({
  email,
  userId,
  loginTimestamp,
}: HubSpotLoginTelemetryPayload): HubSpotLoginTelemetryResult {
  const timestamp = loginTimestamp || Date.now();
  const formattedDate = formatHubspotDateTime(timestamp);

  if (typeof window === 'undefined' || !email) {
    return { totalLogins: 1, ultimoLogin: timestamp, formattedUltimoLogin: formattedDate };
  }

  const keyPrefix = userId || email.toLowerCase();
  const storageKey = `personalab_total_logins_${keyPrefix}`;
  const sessionKey = `personalab_login_tracked_${keyPrefix}`;
  const lastLoginKey = `personalab_last_login_${keyPrefix}`;

  let currentTotal = parseInt(localStorage.getItem(storageKey) || '0', 10);
  if (isNaN(currentTotal) || currentTotal < 0) currentTotal = 0;

  // Evita contar login duplicado se for apenas navegação/refresh da mesma aba/sessão
  const alreadyTrackedInSession = sessionStorage.getItem(sessionKey);
  if (!alreadyTrackedInSession) {
    currentTotal += 1;
    localStorage.setItem(storageKey, currentTotal.toString());
    sessionStorage.setItem(sessionKey, 'true');
  }

  localStorage.setItem(lastLoginKey, timestamp.toString());

  try {
    // 1. Envia diretamente para a fila de telemetria do HubSpot (_hsq)
    // Passamos tanto total_logins quanto total_de_logins para cobrir qualquer variação de nome interno gerado pelo HubSpot
    window._hsq = window._hsq || [];
    window._hsq.push([
      'identify',
      {
        email,
        ultimo_login: formattedDate,
        total_logins: currentTotal,
        total_de_logins: currentTotal,
      },
    ]);
    window._hsq.push(['trackPageView']);

    // 2. Dispara evento unificado na camada de dados (dataLayer)
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'user_login_telemetry',
      userEmail: email,
      ultimo_login: formattedDate,
      total_logins: currentTotal,
      total_de_logins: currentTotal,
    });

    // 3. Submissão direta e silenciosa para a API de Formulários do HubSpot (atualiza propriedades no CRM)
    if (!alreadyTrackedInSession) {
      submitHubspotLoginForm({
        email,
        total_logins: currentTotal,
        ultimo_login: formattedDate,
      }).catch((err) => console.warn('[HubSpot] Falha assíncrona no envio silencioso de login:', err));
    }
  } catch (err) {
    console.warn('[HubSpot] Falha ao registrar telemetria de login (fail-safe):', err);
  }

  return { totalLogins: currentTotal, ultimoLogin: timestamp, formattedUltimoLogin: formattedDate };
}

/**
 * Limpa a marcação de sessão ativa no logout para que o próximo login seja devidamente contabilizado.
 */
export function clearHubspotLoginSession(userIdOrEmail?: string): void {
  if (typeof window === 'undefined') return;
  try {
    if (userIdOrEmail) {
      const keyPrefix = userIdOrEmail.toLowerCase();
      sessionStorage.removeItem(`personalab_login_tracked_${keyPrefix}`);
    } else {
      // Limpa chaves de login tracked
      Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('personalab_login_tracked_')) {
          sessionStorage.removeItem(key);
        }
      });
    }
  } catch (err) {
    console.warn('[HubSpot] Falha ao limpar sessão de login:', err);
  }
}

/**
 * Envia silenciosamente a telemetria de uso do produto (PQL) para o HubSpot CRM.
 * Coleta: email, total_personas, total_contextos, total_conversas, total_insights.
 * Fail-Safe: erros de rede não bloqueiam o fluxo do usuário nem afetam a interface.
 */
export async function submitHubspotUsageForm(data: HubSpotUsagePayload): Promise<boolean> {
  const formId = data.formId !== undefined ? data.formId : HUBSPOT_USAGE_FORM_ID;
  if (!formId) {
    return false;
  }

  try {
    const fields = [
      { name: 'email', value: data.email },
      { name: 'total_personas', value: String(data.total_personas) },
      { name: 'total_contextos', value: String(data.total_contextos) },
      { name: 'total_conversas', value: String(data.total_conversas) },
      { name: 'total_insights', value: String(data.total_insights) },
    ];

    if (data.lifecyclestage) {
      fields.push({ name: 'lifecyclestage', value: data.lifecyclestage });
    }
    if (data.hs_lead_status) {
      fields.push({ name: 'hs_lead_status', value: data.hs_lead_status });
    }
    if (data.closedate) {
      fields.push({ name: 'closedate', value: data.closedate });
    }

    const hutk = getHubspotUtk();

    const payload = {
      fields,
      context: {
        ...(hutk ? { hutk } : {}),
        pageUri: typeof window !== 'undefined' ? window.location.href : 'https://app.chatpersonalab.com.br/dashboard',
        pageName: typeof document !== 'undefined' ? document.title : 'Uso do Produto PersonaLab',
      },
    };

    // 1. Atualiza telemetria no navegador (_hsq e dataLayer)
    if (typeof window !== 'undefined') {
      if (window._hsq) {
        const identifyData: Record<string, unknown> = {
          email: data.email,
          total_personas: data.total_personas,
          total_contextos: data.total_contextos,
          total_conversas: data.total_conversas,
          total_insights: data.total_insights,
        };
        if (data.lifecyclestage) identifyData.lifecyclestage = data.lifecyclestage;
        if (data.hs_lead_status) identifyData.hs_lead_status = data.hs_lead_status;

        window._hsq.push(['identify', identifyData]);
        window._hsq.push(['trackPageView']);
      }
      if (window.dataLayer) {
        window.dataLayer.push({
          event: 'product_usage_telemetry',
          userEmail: data.email,
          total_personas: data.total_personas,
          total_contextos: data.total_contextos,
          total_conversas: data.total_conversas,
          total_insights: data.total_insights,
          lifecyclestage: data.lifecyclestage,
          hs_lead_status: data.hs_lead_status,
        });
      }
    }

    // 2. Submissão direta para a API de Formulários do HubSpot
    const response = await fetch(
      `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${formId}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.warn('[HubSpot] Erro na submissão de telemetria de uso:', response.status, errorText);
      return false;
    }

    return true;
  } catch (err) {
    console.warn('[HubSpot] Falha no envio de telemetria de uso (fail-safe):', err);
    return false;
  }
}

/**
 * Sincroniza as métricas de uso e ciclo de vida com o HubSpot apenas se houver alteração real nos valores.
 * Utiliza cache em localStorage para evitar requisições de rede desnecessárias e repetitivas.
 */
export async function syncHubspotUsageIfChanged(data: HubSpotUsagePayload): Promise<boolean> {
  if (typeof window === 'undefined' || !data.email) {
    return false;
  }

  const keyPrefix = data.userId || data.email.toLowerCase();
  const storageKey = `personalab_synced_usage_${keyPrefix}`;

  const currentSignature = `${data.total_personas}|${data.total_contextos}|${data.total_conversas}|${data.total_insights}|${data.lifecyclestage || ''}|${data.hs_lead_status || ''}`;
  const previousSignature = localStorage.getItem(storageKey);

  if (previousSignature === currentSignature) {
    return true;
  }

  const success = await submitHubspotUsageForm(data);
  if (success) {
    localStorage.setItem(storageKey, currentSignature);
  }
  return success;
}

/**
 * Recupera o número de logins realizados pelo usuário após a expiração do trial.
 */
export function getLoginsAfterExpired(userIdOrEmail: string): number {
  if (typeof window === 'undefined') return 0;
  const keyPrefix = userIdOrEmail.toLowerCase();
  return parseInt(localStorage.getItem(`personalab_logins_after_expired_${keyPrefix}`) || '0', 10);
}

/**
 * Registra um novo login após a expiração do trial (usando sessionStorage para ignorar F5 / reload).
 */
export function recordLoginAfterExpired(userIdOrEmail: string): number {
  if (typeof window === 'undefined') return 0;
  const keyPrefix = userIdOrEmail.toLowerCase();
  const storageKey = `personalab_logins_after_expired_${keyPrefix}`;
  const sessionKey = `personalab_expired_login_counted_${keyPrefix}`;

  let currentCount = parseInt(localStorage.getItem(storageKey) || '0', 10);
  if (!sessionStorage.getItem(sessionKey)) {
    currentCount += 1;
    localStorage.setItem(storageKey, currentCount.toString());
    sessionStorage.setItem(sessionKey, 'true');
  }
  return currentCount;
}


